import React, { useEffect, useState, memo } from 'react';
import { ChefHat, Camera, FileText, BookOpen, Users, HelpCircle, Sparkles } from 'lucide-react';
interface LandingPageProps {
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
  hasSeenAnimation?: boolean;
  onAnimationComplete?: () => void;
}
type FeatureInfo = {
  title: string;
  subtitle: string;
  description: string;
  details: string[];
};
const SLOGANS = ['Measure with your heart', 'A new way to cook with love', 'Where recipes meet relationships', "Cook like nobody's watching", 'From our AI to your table', 'Making memories, one meal at a time', "Your kitchen's new best friend", 'Because every meal tells a story', 'Cooking made personal, again', "Let's make something delicious together"];
const FEATURE_INFO: Record<string, FeatureInfo> = {
  generate: {
    title: 'Generate Recipe',
    subtitle: 'Guided three step recipe generation',
    description: "AI creates custom recipes tailored to your guests' dietary needs and preferences. Just tell us who you're cooking for and what you're craving.",
    details: ['Specify who you will be cooking for - users have configured preferences to help auto-populate requirements', "Tweak preferences - Review your guests' preferences including dietary requirements and flavor profiles", 'Prompt - Explain to the assistant what kind of recipe you would like to generate, including cuisine, ingredients, or cooking methods']
  },
  import: {
    title: 'Import Recipe',
    subtitle: 'Generate instructions to share',
    description: 'Turn photos, handwritten notes, or verbal descriptions into complete, structured recipes with ingredients, instructions, and timing.',
    details: ['Pass in details about your existing recipe including pictures, handwritten notes, or verbal descriptions', 'AI extracts and structures ingredients, instructions, timing, and techniques automatically', 'Refine the recipe instructions through chat until they are exactly right', 'Save the recipe for private use, or share the love with friends and family']
  },
  browse: {
    title: 'Browse Recipes',
    subtitle: "What's cook'n, good look'n?",
    description: 'Explore your collection of recipes and build shopping lists with real-time pricing, stock availability, and aisle locations from your local stores.',
    details: ['Browse the complete list of recipes available to you - including your own creations and those shared by friends and family', 'Filter down the results to find exactly what you are in the mood for by cuisine, difficulty, cook time, or dietary needs', 'Generate revisions of existing recipes to more closely align with the preferences of you and your guests', 'Mark favorites, rate recipes, and leave comments to build your personal cookbook']
  },
  manage: {
    title: 'Manage Guests',
    subtitle: 'Get the family involved',
    description: 'Create profiles for family and friends with their dietary restrictions, allergies, and taste preferences so AI can generate recipes everyone will love.',
    details: ['Create new guest profiles with preferences that your assistant will take into consideration when generating recipes', 'Fill in preferences for your guest or trigger a text to ask them to provide their preferences directly', 'After meals, you can choose to present guests with an opportunity to provide feedback and ratings', 'Track dietary restrictions, allergies, favorite flavors, and ingredients to avoid for each guest']
  }
};
export function LandingPage({
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests,
  hasSeenAnimation = false,
  onAnimationComplete
}: LandingPageProps) {
  const [showIntro, setShowIntro] = useState(!hasSeenAnimation);
  const [showText, setShowText] = useState(false);
  const [shiftUpAndFade, setShiftUpAndFade] = useState(false);
  const [currentSlogan, setCurrentSlogan] = useState(0);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (hasSeenAnimation) {
      return;
    }
    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 2000);
    const shiftAndFadeTimer = setTimeout(() => {
      setShiftUpAndFade(true);
    }, 2200);
    const hideTimer = setTimeout(() => {
      setShowIntro(false);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }, 2700);
    return () => {
      clearTimeout(textTimer);
      clearTimeout(shiftAndFadeTimer);
      clearTimeout(hideTimer);
    };
  }, [hasSeenAnimation, onAnimationComplete]);
  useEffect(() => {
    const sloganInterval = setInterval(() => {
      setCurrentSlogan(prev => (prev + 1) % SLOGANS.length);
    }, 5000);
    return () => clearInterval(sloganInterval);
  }, []);
  const handleFlipCard = (feature: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(feature)) {
        newSet.delete(feature);
      } else {
        newSet.add(feature);
      }
      return newSet;
    });
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] relative overflow-hidden" data-id="element-889">
      {/* Animated Intro WITH LOADING TEXT */}
      {showIntro && <div className={`fixed inset-0 bg-white z-50 flex items-center justify-center transition-all duration-500 ${shiftUpAndFade ? 'opacity-0 -translate-y-[45vh]' : 'opacity-100 translate-y-0'}`} data-id="element-890">
          <div className="flex flex-col items-center" data-id="element-891">
            <div className="flex items-center transition-all duration-500" style={{
          gap: showText ? '0.75rem' : '0'
        }} data-id="element-892">
              <ChefHat className={`text-gray-800 transition-all duration-500 ${!showText ? 'animate-spin w-32 h-32' : 'w-12 h-12'}`} style={{
            animationDuration: '2s',
            animationIterationCount: '1'
          }} data-id="element-893" />
              <h1 className={`text-5xl md:text-6xl font-bold text-gray-900 transition-opacity duration-300 ${showText ? 'opacity-100' : 'opacity-0 w-0'}`} data-id="element-894">
                Cook.AI
              </h1>
            </div>
            {/* Loading text - only visible during spinning animation */}
            {!showText && <p className="mt-4 font-mono text-sm text-gray-400" data-id="element-895">Loading...</p>}
          </div>
        </div>}

      {/* Main Content */}
      <div className={showIntro ? 'opacity-0' : 'opacity-100'} data-id="element-896">
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-24" data-id="element-897">
          {/* Header */}
          <div className="text-center mb-16 space-y-4" data-id="element-898">
            <div className="flex items-center justify-center space-x-3 mb-6" data-id="element-899">
              <ChefHat className="w-12 h-12 text-gray-800" data-id="element-900" />
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900" data-id="element-901">
                Cook.AI
              </h1>
            </div>
            <div className="h-8 flex items-center justify-center" data-id="element-902">
              <p key={currentSlogan} className="text-lg text-gray-600 font-mono max-w-2xl mx-auto animate-fadeIn" data-id="element-903">
                {SLOGANS[currentSlogan]}
              </p>
            </div>
          </div>

          {/* Hero Buttons Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-904">
            {/* Generate Recipe Card */}
            <div className="relative min-h-64" style={{
            perspective: '1000px'
          }} data-id="element-905">
              <div className={`relative w-full transition-transform duration-500 ${flippedCards.has('generate') ? '[transform:rotateY(180deg)]' : ''}`} style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-906">
                <button onClick={e => {
                e.stopPropagation();
                handleFlipCard('generate', e);
              }} className={`${flippedCards.has('generate') ? 'invisible' : 'visible'} absolute inset-0 group border-2 border-gray-300 bg-white hover:border-gray-900 hover:bg-gray-50 transition-all p-6 text-left flex flex-col h-64`} style={{
                backfaceVisibility: 'hidden'
              }} data-id="element-907">
                  <div className="flex items-start justify-between mb-4 w-full" data-id="element-908">
                    <div className="w-16 h-16 border-2 border-gray-300 bg-gray-100 flex items-center justify-center group-hover:border-gray-900 transition-colors flex-shrink-0" data-id="element-909">
                      <Sparkles className="w-8 h-8 text-gray-600 group-hover:text-gray-900" data-id="element-910" />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center" data-id="element-911">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-912">
                      Generate Recipe
                    </h2>
                    <p className="text-base text-gray-600 font-mono leading-relaxed" data-id="element-913">
                      Cooking... but like, easy as hell, bruh.
                    </p>
                  </div>
                </button>

                <div className={`${flippedCards.has('generate') ? 'relative' : 'absolute inset-0'} border-2 border-gray-800 bg-white p-4 flex flex-col cursor-pointer`} style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }} onClick={e => {
                e.stopPropagation();
                handleFlipCard('generate', e);
              }} data-id="element-914">
                  <div className="flex-1" data-id="element-915">
                    <div className="flex items-start justify-between mb-3" data-id="element-916">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-917">
                        {FEATURE_INFO.generate.title}
                      </h3>
                    </div>
                    <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2" data-id="element-918">
                      {FEATURE_INFO.generate.subtitle}
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed mb-3" data-id="element-919">
                      {FEATURE_INFO.generate.description}
                    </p>
                    <div className="space-y-2" data-id="element-920">
                      {FEATURE_INFO.generate.details.map((detail, idx) => <div key={idx} className="flex items-start space-x-2 text-xs text-gray-700" data-id="element-921">
                          <span className="font-mono text-gray-400 flex-shrink-0" data-id="element-922">
                            {(idx + 1).toString().padStart(2, '0')}.
                          </span>
                          <span className="leading-relaxed" data-id="element-923">{detail}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-924">
                    <button onClick={e => {
                    e.stopPropagation();
                    onGenerateRecipe();
                  }} className="w-full px-3 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors font-mono text-sm" data-id="element-925">
                      Let's Cook, Bruh
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Import Recipe Card */}
            <div className="relative min-h-64" style={{
            perspective: '1000px'
          }} data-id="element-926">
              <div className={`relative w-full transition-transform duration-500 ${flippedCards.has('import') ? '[transform:rotateY(180deg)]' : ''}`} style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-927">
                <button onClick={e => {
                e.stopPropagation();
                handleFlipCard('import', e);
              }} className={`${flippedCards.has('import') ? 'invisible' : 'visible'} absolute inset-0 group border-2 border-gray-200 bg-gray-50 p-6 text-left flex flex-col overflow-hidden h-64`} style={{
                backfaceVisibility: 'hidden'
              }} data-id="element-928">
                  {/* Diagonal Coming Soon Badge */}
                  <div className="absolute -left-12 top-8 z-10 rotate-[-45deg] w-48" data-id="element-929">
                    <div className="bg-gray-300 text-gray-600 font-mono text-xs uppercase tracking-widest py-1 text-center" data-id="element-930">
                      Coming Soon
                    </div>
                  </div>

                  <div className="flex items-start justify-between mb-4 w-full" data-id="element-931">
                    <div className="w-16 h-16 border-2 border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-932">
                      <Camera className="w-8 h-8 text-gray-400" data-id="element-933" />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center" data-id="element-934">
                    <h2 className="text-3xl font-bold text-gray-400 mb-2" data-id="element-935">
                      Import Recipe
                    </h2>
                    <p className="text-base text-gray-400 font-mono leading-relaxed" data-id="element-936">
                      Snap a pic of that recipe card and let it transform into a
                      full-blown Betty Crocker wonder.
                    </p>
                  </div>
                </button>

                <div className={`${flippedCards.has('import') ? 'relative' : 'absolute inset-0'} border-2 border-gray-800 bg-white p-4 flex flex-col cursor-pointer`} style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }} onClick={e => {
                e.stopPropagation();
                handleFlipCard('import', e);
              }} data-id="element-937">
                  <div className="flex-1" data-id="element-938">
                    <div className="flex items-start justify-between mb-3" data-id="element-939">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-940">
                        {FEATURE_INFO.import.title}
                      </h3>
                    </div>
                    <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2" data-id="element-941">
                      {FEATURE_INFO.import.subtitle}
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed mb-3" data-id="element-942">
                      {FEATURE_INFO.import.description}
                    </p>
                    <div className="space-y-2" data-id="element-943">
                      {FEATURE_INFO.import.details.map((detail, idx) => <div key={idx} className="flex items-start space-x-2 text-xs text-gray-700" data-id="element-944">
                          <span className="font-mono text-gray-400 flex-shrink-0" data-id="element-945">
                            {(idx + 1).toString().padStart(2, '0')}.
                          </span>
                          <span className="leading-relaxed" data-id="element-946">{detail}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-947">
                    <button onClick={e => {
                    e.stopPropagation();
                    onImportRecipe();
                  }} className="w-full px-3 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors font-mono text-sm opacity-50 cursor-not-allowed" disabled data-id="element-948">
                      Snap That Card
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Browse Recipes Card */}
            <div className="relative min-h-64" style={{
            perspective: '1000px'
          }} data-id="element-949">
              <div className={`relative w-full transition-transform duration-500 ${flippedCards.has('browse') ? '[transform:rotateY(180deg)]' : ''}`} style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-950">
                <button onClick={e => {
                e.stopPropagation();
                handleFlipCard('browse', e);
              }} className={`${flippedCards.has('browse') ? 'invisible' : 'visible'} absolute inset-0 group border-2 border-gray-300 bg-white hover:border-gray-900 hover:bg-gray-50 transition-all p-6 text-left flex flex-col h-64`} style={{
                backfaceVisibility: 'hidden'
              }} data-id="element-951">
                  <div className="flex items-start justify-between mb-4 w-full" data-id="element-952">
                    <div className="w-16 h-16 border-2 border-gray-300 bg-gray-100 flex items-center justify-center group-hover:border-gray-900 transition-colors flex-shrink-0" data-id="element-953">
                      <BookOpen className="w-8 h-8 text-gray-600 group-hover:text-gray-900" data-id="element-954" />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center" data-id="element-955">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-956">
                      Browse Recipes
                    </h2>
                    <p className="text-base text-gray-600 font-mono leading-relaxed" data-id="element-957">
                      Why be the lab rat? Someone already tested these!
                    </p>
                  </div>
                </button>

                <div className={`${flippedCards.has('browse') ? 'relative' : 'absolute inset-0'} border-2 border-gray-800 bg-white p-4 flex flex-col cursor-pointer`} style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }} onClick={e => {
                e.stopPropagation();
                handleFlipCard('browse', e);
              }} data-id="element-958">
                  <div className="flex-1" data-id="element-959">
                    <div className="flex items-start justify-between mb-3" data-id="element-960">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-961">
                        {FEATURE_INFO.browse.title}
                      </h3>
                    </div>
                    <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2" data-id="element-962">
                      {FEATURE_INFO.browse.subtitle}
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed mb-3" data-id="element-963">
                      {FEATURE_INFO.browse.description}
                    </p>
                    <div className="space-y-2" data-id="element-964">
                      {FEATURE_INFO.browse.details.map((detail, idx) => <div key={idx} className="flex items-start space-x-2 text-xs text-gray-700" data-id="element-965">
                          <span className="font-mono text-gray-400 flex-shrink-0" data-id="element-966">
                            {(idx + 1).toString().padStart(2, '0')}.
                          </span>
                          <span className="leading-relaxed" data-id="element-967">{detail}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-968">
                    <button onClick={e => {
                    e.stopPropagation();
                    onBrowseRecipes();
                  }} className="w-full px-3 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors font-mono text-sm" data-id="element-969">
                      Show Me The Goods
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Manage Guests Card */}
            <div className="relative min-h-64" style={{
            perspective: '1000px'
          }} data-id="element-970">
              <div className={`relative w-full transition-transform duration-500 ${flippedCards.has('manage') ? '[transform:rotateY(180deg)]' : ''}`} style={{
              transformStyle: 'preserve-3d'
            }} data-id="element-971">
                <button onClick={e => {
                e.stopPropagation();
                handleFlipCard('manage', e);
              }} className={`${flippedCards.has('manage') ? 'invisible' : 'visible'} absolute inset-0 group border-2 border-gray-300 bg-white hover:border-gray-900 hover:bg-gray-50 transition-all p-6 text-left flex flex-col h-64`} style={{
                backfaceVisibility: 'hidden'
              }} data-id="element-972">
                  <div className="flex items-start justify-between mb-4 w-full" data-id="element-973">
                    <div className="w-16 h-16 border-2 border-gray-300 bg-gray-100 flex items-center justify-center group-hover:border-gray-900 transition-colors flex-shrink-0" data-id="element-974">
                      <Users className="w-8 h-8 text-gray-600 group-hover:text-gray-900" data-id="element-975" />
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center" data-id="element-976">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-977">
                      Manage Guests
                    </h2>
                    <p className="text-base text-gray-600 font-mono leading-relaxed" data-id="element-978">
                      Picky eaters get a special box on this app.
                    </p>
                  </div>
                </button>

                <div className={`${flippedCards.has('manage') ? 'relative' : 'absolute inset-0'} border-2 border-gray-800 bg-white p-4 flex flex-col cursor-pointer`} style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }} onClick={e => {
                e.stopPropagation();
                handleFlipCard('manage', e);
              }} data-id="element-979">
                  <div className="flex-1" data-id="element-980">
                    <div className="flex items-start justify-between mb-3" data-id="element-981">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-982">
                        {FEATURE_INFO.manage.title}
                      </h3>
                    </div>
                    <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2" data-id="element-983">
                      {FEATURE_INFO.manage.subtitle}
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed mb-3" data-id="element-984">
                      {FEATURE_INFO.manage.description}
                    </p>
                    <div className="space-y-2" data-id="element-985">
                      {FEATURE_INFO.manage.details.map((detail, idx) => <div key={idx} className="flex items-start space-x-2 text-xs text-gray-700" data-id="element-986">
                          <span className="font-mono text-gray-400 flex-shrink-0" data-id="element-987">
                            {(idx + 1).toString().padStart(2, '0')}.
                          </span>
                          <span className="leading-relaxed" data-id="element-988">{detail}</span>
                        </div>)}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-989">
                    <button onClick={e => {
                    e.stopPropagation();
                    onManageGuests();
                  }} className="w-full px-3 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors font-mono text-sm" data-id="element-990">
                      Wrangle The Picky Ones
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-16 text-center" data-id="element-991">
            <p className="font-mono text-xs text-gray-400" data-id="element-992">
              © {new Date().getFullYear()} COOK.AI // WIREFRAME MODE
            </p>
          </div>
        </div>
      </div>
    </div>;
}